import React, { useEffect, useId, useMemo, useState } from 'react';
import Modal from './Modal';
import { documentsApi, MedicalDocument, MedicalDocumentUpdatePayload } from '../api/documents';
import { formatApiError } from '../utils/errorUtils';
import { useIsAdvanced } from '../utils/viewMode';

export interface DocumentMetadataModalProps {
  open: boolean;
  onClose: () => void;
  document: MedicalDocument | null;
  onSaved: () => void;
}

function toDateInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const DocumentMetadataModal: React.FC<DocumentMetadataModalProps> = ({
  open,
  onClose,
  document,
  onSaved,
}) => {
  const isAdvanced = useIsAdvanced();
  const formId = useId();
  const [originalFilename, setOriginalFilename] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  // Comma-separated text input for the advanced view; the simplified
  // view uses a single-select dropdown that we wrap in a one-element
  // array on save. Both flows persist as `string[]` server-side.
  // Re-classification will overwrite these values from the classifier
  // output — manual edits stick only until the next reclassify.
  const [documentTypesText, setDocumentTypesText] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && document) {
      setOriginalFilename(document.original_filename || '');
      setDocumentDate(toDateInputValue(document.document_date));
      setDocumentTypesText((document.document_types ?? []).join(', '));
      setNotes(document.notes || '');
      setError('');
    }
  }, [open, document]);

  const parsedTypes = useMemo(
    () =>
      documentTypesText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [documentTypesText],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!document) return;
    const name = originalFilename.trim();
    if (!name) {
      setError('Numele fișierului nu poate fi gol');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: MedicalDocumentUpdatePayload = {
        original_filename: name,
        document_types: parsedTypes.length > 0 ? parsedTypes : null,
        notes: notes.trim() || null,
      };
      if (documentDate) {
        payload.document_date = new Date(documentDate + 'T12:00:00').toISOString();
      } else {
        payload.document_date = null;
      }
      await documentsApi.updateDocument(document.id, payload);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Editează detalii document"
      footer={
        document ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Anulează
            </button>
            <button type="submit" form={formId} className="btn btn-primary" disabled={saving}>
              {saving ? 'Se salvează…' : 'Salvează'}
            </button>
          </>
        ) : null
      }
    >
      {document && (
        <form id={formId} onSubmit={handleSubmit}>
          {error && <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>{error}</div>}
          {/* Renaming a file is useful for non-tech users too — e.g. turning
              "IMG_20240301_182455.jpg" into "Analize colesterol martie". Keep it
              in both modes. */}
          <div className="form-group">
            <label className="form-label">{isAdvanced ? 'Nume afișat' : 'Nume document'}</label>
            <input
              className="form-input"
              type="text"
              value={originalFilename}
              onChange={(e) => setOriginalFilename(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Data documentului</label>
            <input
              className="form-input"
              type="date"
              value={documentDate}
              onChange={(e) => setDocumentDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              {isAdvanced ? 'Tipuri document (separate prin virgulă)' : 'Tip'}
            </label>
            {isAdvanced ? (
              <input
                className="form-input"
                type="text"
                value={documentTypesText}
                onChange={(e) => setDocumentTypesText(e.target.value)}
                placeholder="ex: Raport de laborator, Notă clinică"
              />
            ) : (
              <select
                className="form-input"
                value={parsedTypes[0] ?? ''}
                onChange={(e) => setDocumentTypesText(e.target.value)}
              >
                <option value="">— alege —</option>
                <option value="Analize">Analize</option>
                <option value="Rețetă">Rețetă</option>
                <option value="Consultație">Consultație</option>
                <option value="Imagistică">Imagistică (RMN, CT, ecografie)</option>
                <option value="Raport medical">Raport medical</option>
                <option value="Altele">Altele</option>
              </select>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Note</label>
            <textarea
              className="form-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Descriere scurtă; reclasificarea poate suprascrie acest câmp."
            />
          </div>
        </form>
      )}
    </Modal>
  );
};

export default DocumentMetadataModal;
